/**
 * Resolución de feature-overrides por alcance (AUTONOMY · Layer 1).
 *
 * El catálogo declara features con `scope` platform|org|course. El estado
 * efectivo se resuelve en cascada **first-defined-wins**:
 *
 *   course (`course_settings.features`) → org (`org_settings.features`)
 *   → plataforma (`feature_flags.enabled`) → habilitada por defecto.
 *
 * Los overrides se guardan como JSON `{ "<key>": boolean }`; una key AUSENTE
 * significa «hereda del nivel superior» (jamás se persiste el default). Este
 * módulo es la única fuente de esa semántica en TS (la UI de ajustes de org y
 * de curso la consumen); el server sigue siendo autoridad de las escrituras
 * vía write-path (authz por org/curso).
 */

import { allManifestSources, type ManifestDescriptor } from "../feature-manifests";
import type { FeatureManifest } from "./country/packs";

/** Overrides de un alcance: key del catálogo → habilitada sí/no. */
export type FeatureOverrides = Readonly<Record<string, boolean>>;

/**
 * Parsea la columna jsonb (`features`) sincronizada como texto. Tolerante:
 * cualquier forma inesperada ⇒ sin overrides (nunca rompe el arranque).
 */
export function parseFeatureOverrides(raw: string | null | undefined): FeatureOverrides {
  if (!raw) {
    return {};
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
      return {};
    }
    const out: Record<string, boolean> = {};
    for (const [key, value] of Object.entries(parsed)) {
      if (typeof value === "boolean") {
        out[key] = value;
      }
    }
    return out;
  } catch {
    return {};
  }
}

/** Serializa overrides para la columna `features` (objeto plano estable). */
export function serializeFeatureOverrides(overrides: FeatureOverrides): string {
  const entries = Object.entries(overrides).toSorted(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
  return JSON.stringify(Object.fromEntries(entries));
}

export interface FeatureResolutionInput {
  /** Estado del flag de plataforma (undefined = sin fila ⇒ habilitada). */
  platform?: boolean;
  /** Overrides de la organización (`org_settings.features`). */
  org?: FeatureOverrides;
  /** Overrides del curso (`course_settings.features`). */
  course?: FeatureOverrides;
}

/**
 * Estado efectivo de una feature: curso → org → plataforma → `true`.
 * First-defined-wins: el primer alcance que DEFINE la key decide.
 */
export function resolveFeatureEnabled(key: string, input: FeatureResolutionInput): boolean {
  const fromCourse = input.course?.[key];
  if (fromCourse !== undefined) {
    return fromCourse;
  }
  const fromOrg = input.org?.[key];
  if (fromOrg !== undefined) {
    return fromOrg;
  }
  return input.platform ?? true;
}

// ── FLAGS · grafo de dependencias (espejo TS de `platform/features.rs`) ───────

/** Una alternativa de una cláusula `requires`, parseada. */
export interface RequireAlt {
  key: string;
  /** `true` si lleva sufijo `?` (dependencia blanda: compone en gates, no bloquea toggles). */
  soft: boolean;
}

/** Parsea una cláusula `requires` (`"a"`, `"a?"`, `"a|b|c"`). */
export function parseRequireClause(clause: string): RequireAlt[] {
  return clause
    .split("|")
    .filter((s) => s.length > 0)
    .map((alt) =>
      alt.endsWith("?") ? { key: alt.slice(0, -1), soft: true } : { key: alt, soft: false },
    );
}

/** Nodo del grafo de features del lado TS (manifest completo). */
export interface FeatureGraphNode {
  key: string;
  name: string;
  scope: "platform" | "org" | "course";
  requires: string[];
  conflicts: string[];
  /** Estado de fábrica declarado en el manifest (§L1). Ausente ⇒ `true`. */
  defaultEnabled: boolean;
}

/** Grafo TS: todas las features del manifest (cualquier categoría/scope). */
export function featureGraph(): Map<string, FeatureGraphNode> {
  const graph = new Map<string, FeatureGraphNode>();
  for (const src of allManifestSources()) {
    for (const m of src.manifests) {
      graph.set(m.key, {
        key: m.key,
        name: m.name,
        scope: m.scope,
        requires: m.requires ?? [],
        conflicts: m.conflicts ?? [],
        defaultEnabled: m.defaultEnabled ?? true,
      });
    }
  }
  return graph;
}

/**
 * Estado EFECTIVO de una feature: resuelta Y todas sus cláusulas `requires`
 * (duras y blandas) satisfechas, recursivo con guarda de ciclos. Devuelve la
 * key culpable cuando está deshabilitada (puede ser un requisito).
 */
export function resolveFeatureEffective(
  key: string,
  input: FeatureResolutionInput & { platformMap?: Readonly<Record<string, boolean>> },
  graph: ReadonlyMap<string, FeatureGraphNode> = featureGraph(),
): { enabled: boolean; blockedBy?: string } {
  const resolved = (k: string): boolean => {
    const fromCourse = input.course?.[k];
    if (fromCourse !== undefined) {
      return fromCourse;
    }
    const fromOrg = input.org?.[k];
    if (fromOrg !== undefined) {
      return fromOrg;
    }
    const fromPlatform = input.platformMap?.[k];
    if (fromPlatform !== undefined) {
      return fromPlatform;
    }
    // Compat: `platform` escalar solo aplica a la key raíz consultada.
    if (k === key && input.platform !== undefined) {
      return input.platform;
    }
    // §L1: nadie la ha tocado ⇒ manda el default DECLARADO en el manifest
    // (ausente ⇒ true). Sin esto, una feature nacida apagada aparecería
    // encendida en el cliente hasta que el server reconciliara su fila.
    return graph.get(k)?.defaultEnabled ?? true;
  };
  const visiting = new Set<string>();
  const effective = (k: string): string | undefined => {
    if (visiting.has(k)) {
      return undefined; // ciclo: no re-evaluar (la validación del catálogo lo denuncia)
    }
    visiting.add(k);
    if (!resolved(k)) {
      return k;
    }
    const node = graph.get(k);
    if (node) {
      for (const clause of node.requires) {
        const alts = parseRequireClause(clause);
        if (alts.length === 0) {
          continue;
        }
        let satisfied = false;
        let lastBlocked: string | undefined;
        for (const alt of alts) {
          const blocked = effective(alt.key);
          if (blocked === undefined) {
            satisfied = true;
            break;
          }
          lastBlocked = blocked;
        }
        if (!satisfied) {
          return lastBlocked;
        }
      }
    }
    return undefined;
  };
  const blockedBy = effective(key);
  return blockedBy === undefined ? { enabled: true } : { enabled: false, blockedBy };
}

/** Violación DURA del grafo en un estado dado (espejo de `Violation` Rust). */
export type FeatureViolation =
  | { kind: "missing-requirement"; dependent: string; clause: string }
  | { kind: "conflict"; a: string; b: string };

/**
 * Violaciones duras de un estado (`isEnabled` = resolución cruda por key).
 * Las cláusulas 100% blandas no bloquean toggles.
 */
export function stateViolations(
  graph: ReadonlyMap<string, FeatureGraphNode>,
  isEnabled: (key: string) => boolean,
): FeatureViolation[] {
  const out: FeatureViolation[] = [];
  const conflictPairs = new Map<string, { a: string; b: string }>();
  for (const node of graph.values()) {
    if (!isEnabled(node.key)) {
      continue;
    }
    for (const clause of node.requires) {
      const alts = parseRequireClause(clause);
      if (alts.length === 0 || alts.every((a) => a.soft)) {
        continue;
      }
      if (!alts.some((a) => isEnabled(a.key))) {
        out.push({ kind: "missing-requirement", dependent: node.key, clause });
      }
    }
    for (const c of node.conflicts) {
      if (isEnabled(c)) {
        const [a, b] = node.key < c ? [node.key, c] : [c, node.key];
        conflictPairs.set(`${a}::${b}`, { a, b });
      }
    }
  }
  for (const id of [...conflictPairs.keys()].toSorted()) {
    const pair = conflictPairs.get(id);
    if (pair) {
      out.push({ kind: "conflict", a: pair.a, b: pair.b });
    }
  }
  return out;
}

/**
 * Delta de violaciones: las del estado NUEVO que no existían en el VIEJO
 * (un estado heredado imperfecto no bloquea arreglos). Espejo EXACTO de la
 * validación server-side de los tres write-paths.
 */
function violationFingerprint(v: FeatureViolation): string {
  return v.kind === "missing-requirement" ? `r:${v.dependent}:${v.clause}` : `c:${v.a}:${v.b}`;
}

export function newViolations(
  graph: ReadonlyMap<string, FeatureGraphNode>,
  oldEnabled: (key: string) => boolean,
  nextEnabled: (key: string) => boolean,
): FeatureViolation[] {
  const old = new Set(stateViolations(graph, oldEnabled).map(violationFingerprint));
  return stateViolations(graph, nextEnabled).filter((v) => !old.has(violationFingerprint(v)));
}

/** Sub-grafo con solo features de scope org|course (vista del editor de org/curso). */
function orgScopedGraph(graph: ReadonlyMap<string, FeatureGraphNode>) {
  return new Map([...graph.entries()].filter(([, n]) => n.scope !== "platform"));
}

/**
 * Valida un cambio de overrides de ORG (mismo delta que el write-path server).
 * `platform` = mapa de flags de plataforma (ausente ⇒ habilitada).
 */
export function validateOrgOverridesChange(args: {
  graph?: ReadonlyMap<string, FeatureGraphNode>;
  platform: Readonly<Record<string, boolean>>;
  oldOrg: FeatureOverrides;
  newOrg: FeatureOverrides;
}): FeatureViolation[] {
  const full = args.graph ?? featureGraph();
  const graph = orgScopedGraph(full);
  const resolve =
    (org: FeatureOverrides) =>
    (key: string): boolean =>
      org[key] ?? args.platform[key] ?? full.get(key)?.defaultEnabled ?? true;
  return newViolations(graph, resolve(args.oldOrg), resolve(args.newOrg));
}

/** Ídem para un cambio de overrides de CURSO (fondo = plataforma ⊕ org). */
export function validateCourseOverridesChange(args: {
  graph?: ReadonlyMap<string, FeatureGraphNode>;
  platform: Readonly<Record<string, boolean>>;
  org: FeatureOverrides;
  oldCourse: FeatureOverrides;
  newCourse: FeatureOverrides;
}): FeatureViolation[] {
  const full = args.graph ?? featureGraph();
  const graph = orgScopedGraph(full);
  const resolve =
    (course: FeatureOverrides) =>
    (key: string): boolean =>
      course[key] ?? args.org[key] ?? args.platform[key] ?? full.get(key)?.defaultEnabled ?? true;
  return newViolations(graph, resolve(args.oldCourse), resolve(args.newCourse));
}

/** ¿Quién depende (arista dura o blanda) de `key`? Para la vista inversa. */
export function dependentsOf(
  key: string,
  graph: ReadonlyMap<string, FeatureGraphNode> = featureGraph(),
): FeatureGraphNode[] {
  return [...graph.values()].filter((n) =>
    n.requires.some((clause) => parseRequireClause(clause).some((a) => a.key === key)),
  );
}

/** Mensaje humano de una violación (nombres del manifest cuando existen). */
export function violationLabel(
  v: FeatureViolation,
  graph: ReadonlyMap<string, FeatureGraphNode> = featureGraph(),
): string {
  const label = (k: string) => {
    const name = graph.get(k)?.name;
    return name ? `«${name}» (${k})` : `«${k}»`;
  };
  if (v.kind === "conflict") {
    return `${label(v.a)} es incompatible con ${label(v.b)}`;
  }
  const opts = parseRequireClause(v.clause)
    .map((a) => label(a.key))
    .join(" o ");
  return `${label(v.dependent)} requiere ${opts} habilitada`;
}

/**
 * Categorías donde un toggle on/off NO tiene sentido porque ya existe un
 * selector dedicado (país en ajustes, escala en su select): se excluyen del
 * editor de overrides para no ofrecer palancas sin efecto.
 */
const NON_TOGGLABLE_CATEGORIES = new Set(["country_pack", "grading_scheme"]);

/**
 * Features que una ORGANIZACIÓN puede sobreescribir: las de scope `org` y
 * `course` del manifest TS (registries auto-descriptivos). Ordenadas por
 * categoría+key para una UI estable.
 */
export function orgConfigurableFeatures(): ManifestDescriptor[] {
  return allManifestSources()
    .filter((src) => !NON_TOGGLABLE_CATEGORIES.has(src.category))
    .flatMap((src) =>
      src.manifests
        .filter((m) => m.scope === "org" || m.scope === "course")
        .map((m) => toEditorDescriptor(m, src.category)),
    )
    .toSorted((a, b) =>
      a.category === b.category ? (a.key < b.key ? -1 : 1) : a.category < b.category ? -1 : 1,
    );
}

/**
 * Features que un CURSO puede sobreescribir: solo las de scope `course`
 * (el editor de curso refina lo que la org dejó habilitado).
 */
export function courseConfigurableFeatures(): ManifestDescriptor[] {
  return orgConfigurableFeatures().filter((m) => m.scope === "course");
}

/** Descriptor completo para los editores (incluye grafo e implicaciones). */
function toEditorDescriptor(
  m: FeatureManifest,
  category: ManifestDescriptor["category"],
): ManifestDescriptor {
  const d: ManifestDescriptor = {
    key: m.key,
    name: m.name,
    description: m.description,
    category,
    scope: m.scope,
    status: m.status === "experimental" ? ("beta" as const) : m.status,
  };
  if (m.requires && m.requires.length > 0) {
    d.requires = m.requires;
  }
  if (m.conflicts && m.conflicts.length > 0) {
    d.conflicts = m.conflicts;
  }
  if (m.gateable) {
    d.gateable = true;
  }
  if (m.defaultEnabled === false) {
    d.defaultEnabled = false;
  }
  if (m.since) {
    d.since = m.since;
  }
  if (m.docsSlug) {
    d.docsUrl = `/help/${m.docsSlug}`;
  }
  if (m.implications) {
    d.implications = {
      dataPolicy: "preserved",
      ...(m.implications.navHidden?.length ? { navHidden: m.implications.navHidden } : {}),
      ...(m.implications.flowsBlocked?.length ? { flowsBlocked: m.implications.flowsBlocked } : {}),
    };
  }
  return d;
}
